import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { PanelLeft } from "lucide-react";

const BUTTON_SIZE = 52;
const EDGE_GAP = 12;
const RESTING_BOTTOM_GAP = 88;

function visualViewportTop() {
  if (typeof window === "undefined") return 0;
  const vv = window.visualViewport;
  if (!vv) return Math.max(72, window.innerHeight - RESTING_BOTTOM_GAP - BUTTON_SIZE);

  const keyboardLikelyOpen = vv.height < window.innerHeight * 0.78;
  const bottomGap = keyboardLikelyOpen ? EDGE_GAP : RESTING_BOTTOM_GAP;
  return Math.max(
    72,
    Math.round(vv.offsetTop + vv.height - BUTTON_SIZE - bottomGap),
  );
}

export default function MobileLongformTreeButton() {
  const [mobile, setMobile] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [treeOpen, setTreeOpen] = useState(false);
  const [top, setTop] = useState(() => visualViewportTop());

  const syncLayout = useCallback(() => {
    if (typeof document === "undefined") return;
    const isMobile = window.matchMedia?.("(max-width: 768px)")?.matches ?? window.innerWidth <= 768;
    const modal = document.querySelector(".text-editor-modal");
    const legacyLauncher = modal?.querySelector('[aria-label="Abrir árbol del proyecto"]');

    // Mobile gets one keyboard-safe launcher only. Desktop keeps the header control.
    if (legacyLauncher instanceof HTMLElement) {
      legacyLauncher.style.display = isMobile ? "none" : "flex";
    }

    setMobile(isMobile);
    setEditorOpen(Boolean(modal));
    setTreeOpen(Boolean(document.querySelector('[aria-label="Árbol del proyecto"]')));
    setTop(visualViewportTop());
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;

    syncLayout();
    const observer = new MutationObserver(syncLayout);
    observer.observe(document.body, { childList: true, subtree: true });

    const vv = window.visualViewport;
    window.addEventListener("resize", syncLayout);
    window.addEventListener("orientationchange", syncLayout);
    vv?.addEventListener("resize", syncLayout);
    vv?.addEventListener("scroll", syncLayout);
    document.addEventListener("focusin", syncLayout);
    document.addEventListener("focusout", syncLayout);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncLayout);
      window.removeEventListener("orientationchange", syncLayout);
      vv?.removeEventListener("resize", syncLayout);
      vv?.removeEventListener("scroll", syncLayout);
      document.removeEventListener("focusin", syncLayout);
      document.removeEventListener("focusout", syncLayout);
      const launcher = document.querySelector('.text-editor-modal [aria-label="Abrir árbol del proyecto"]');
      if (launcher instanceof HTMLElement) launcher.style.display = "flex";
    };
  }, [syncLayout]);

  const openTree = useCallback(() => {
    const launcher = document.querySelector(
      '.text-editor-modal [aria-label="Abrir árbol del proyecto"]',
    );
    if (!launcher) return;

    // Fire before focus/keyboard changes can move the button under the finger.
    launcher.click();
    window.requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active instanceof HTMLElement) active.blur();
    });
  }, []);

  if (!mobile || !editorOpen || treeOpen || typeof document === "undefined") return null;

  return createPortal(
    <button
      type="button"
      aria-label="Abrir archivos del proyecto"
      title="Archivos del proyecto"
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
        openTree();
      }}
      onClick={(event) => {
        // Keyboard/accessibility fallback; pointer users already fired on pointerdown.
        if (event.detail !== 0) return;
        event.preventDefault();
        openTree();
      }}
      style={{
        position: "fixed",
        zIndex: 13950,
        left: `max(${EDGE_GAP}px, env(safe-area-inset-left))`,
        top,
        width: BUTTON_SIZE,
        height: BUTTON_SIZE,
        borderRadius: 16,
        border: "1px solid rgba(212,175,55,0.52)",
        background: "rgba(18,18,18,0.94)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        color: "#D4AF37",
        boxShadow: "0 8px 28px rgba(0,0,0,0.42)",
        display: "grid",
        placeItems: "center",
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <PanelLeft size={24} />
    </button>,
    document.body,
  );
}
